import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { describeError } from '../../lib/api.ts';
import { api, type ProjectHistoryPage } from '../../lib/client.ts';
import { PIPELINES_QUERY } from '../../lib/record-data.ts';
import { ChangeValues, showValue } from '../records/change-values.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';

type Entry = ProjectHistoryPage['items'][number];

const KIND: Record<Entry['entityType'], string> = {
  projects: 'Project',
  deals: 'Deal',
  jobs: 'Job',
  project_contacts: 'Contact',
  project_pipelines: 'Pipeline',
};

/**
 * The History tab (RELAY-952): every audited change to the project, its deals, its jobs and its
 * contacts, newest first, each line naming what it was about.
 */
export function ProjectHistory({
  projectId,
  labels,
}: {
  projectId: string;
  /** Deal and job titles by id, for the line's badge. */
  labels: ReadonlyMap<string, string>;
}) {
  const q = useInfiniteQuery({
    queryKey: ['project-history', projectId],
    queryFn: ({ pageParam }) => api.projects.history(projectId, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  // A stage move is stored as stage ids; show the stage's name (stages are never hard-deleted).
  const pipelines = useQuery({ queryKey: PIPELINES_QUERY, queryFn: () => api.pipelines.list() });
  const stageNames = new Map(
    (pipelines.data ?? []).flatMap((p) => p.stages.map((s) => [s.id, s.name] as const)),
  );
  // A pipeline joined, moved in or left (TS-1747) names it by id; show its name.
  const pipelineNames = new Map((pipelines.data ?? []).map((p) => [p.id, p.name] as const));
  const value = (field: string, v: unknown) =>
    field === 'stageId' && typeof v === 'string'
      ? (stageNames.get(v) ?? v)
      : field === 'pipelineId' && typeof v === 'string'
        ? (pipelineNames.get(v) ?? v)
        : showValue(v);
  if (q.isPending)
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </p>
    );
  if (q.error)
    return (
      <p className="text-danger text-sm" role="alert">
        {describeError(q.error)}
      </p>
    );
  return (
    <div className="flex flex-col gap-2" data-testid="project-history">
      {items.length === 0 && <p className="text-muted-foreground text-sm">No changes yet.</p>}
      <ol className="flex flex-col gap-2">
        {items.map((e) => (
          <li
            key={e.id}
            className="border-border bg-card rounded-xl border p-3 text-sm"
            data-testid="history-entry"
          >
            <div className="text-muted-foreground mb-1 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">
                {KIND[e.entityType]}
                {e.entityId && labels.get(e.entityId) ? `: ${labels.get(e.entityId)}` : ''}
              </Badge>
              <span className="text-foreground font-medium">{e.actor.label ?? 'System'}</span>
              <span>{e.action}</span>
              <time dateTime={e.occurredAt}>{new Date(e.occurredAt).toLocaleString()}</time>
            </div>
            {e.changes && (
              <ul className="flex flex-col gap-0.5">
                {Object.entries(e.changes).map(([field, c]) => (
                  <li key={field}>
                    <span className="font-medium">{field}</span>:{' '}
                    <ChangeValues change={c} format={(v) => value(field, v)} />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {q.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          disabled={q.isFetchingNextPage}
          onClick={() => void q.fetchNextPage()}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
